import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import {
  FiCalendar,
  FiClock,
  FiLink,
  FiBookOpen,
  FiUsers,
  FiVideo,
  FiRefreshCcw,
  FiSearch,
} from 'react-icons/fi'
import { fetchStaffDashboard } from '../../store/slices/teacherSlice'
import './Lectures.css'

const formatLectureTiming = (startTime, endTime) => {
  const start = new Date(startTime)
  const end = endTime ? new Date(endTime) : null
  const dateLabel = start.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
  const startLabel = start.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  })
  const duration = end ? `${Math.max(1, Math.round((end - start) / 60000))} min` : '—'
  return {
    slot: `${dateLabel} · ${startLabel}`,
    duration,
    raw: start,
  }
}

const Lectures = () => {
  const dispatch = useDispatch()
  const { dashboard, isLoading, error, lastUpdated } = useSelector((state) => state.teacher)
  const [activeView, setActiveView] = useState('upcoming')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    if (!dashboard && !isLoading) {
      dispatch(fetchStaffDashboard())
    }
  }, [dashboard, isLoading, dispatch])

  const formattedUpcoming = useMemo(() => {
    if (!dashboard?.upcomingLectures?.length) return []
    return dashboard.upcomingLectures.map((lecture) => {
      const timing = formatLectureTiming(lecture.startTime, lecture.endTime)
  const batchName = lecture.module?.batch?.batchName || lecture.batch?.batchName || 'Unassigned batch'
      const courseTitle = lecture.module?.batch?.course?.title || lecture.course?.title || 'Course TBC'
      return {
        id: lecture._id,
        topic: lecture.topic || 'Untitled session',
        module: lecture.module?.title || lecture.title || 'Module detail forthcoming',
        batch: batchName,
        course: courseTitle,
        timing,
        deliveryMode: lecture.deliveryMode || 'Live',
        meetingUrl: lecture.meeting?.meetingUrl || lecture.meetingLink || null,
        status: 'Upcoming',
      }
    })
  }, [dashboard])

  const formattedRecent = useMemo(() => {
    if (!dashboard?.recentLectures?.length) return []
    return dashboard.recentLectures.map((lecture) => {
      const timing = formatLectureTiming(lecture.startTime, lecture.endTime)
  const batchName = lecture.module?.batch?.batchName || lecture.batch?.batchName || 'Unassigned batch'
      const courseTitle = lecture.module?.batch?.course?.title || lecture.course?.title || 'Course TBC'
      return {
        id: lecture._id,
        topic: lecture.topic || 'Untitled session',
        module: lecture.module?.title || lecture.title || 'Module detail forthcoming',
        batch: batchName,
        course: courseTitle,
        timing,
        deliveryMode: lecture.deliveryMode || 'Live',
        recordingUrl: lecture.recordingUrl || lecture.resources?.recording || null,
        status: 'Completed',
      }
    })
  }, [dashboard])

  const baseList = activeView === 'upcoming' ? formattedUpcoming : formattedRecent

  const filteredList = useMemo(() => {
    if (!searchQuery.trim()) return baseList
    const query = searchQuery.trim().toLowerCase()
    return baseList.filter((lecture) =>
      [lecture.topic, lecture.module, lecture.batch, lecture.course]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(query))
    )
  }, [baseList, searchQuery])

  const stats = useMemo(() => {
    const upcomingCount = formattedUpcoming.length
    const recentCount = formattedRecent.length
  const studentReach = dashboard?.stats?.totalStudents ?? 0
    const moduleCount = dashboard?.stats?.totalModules ?? 0
    return [
      {
        label: 'Upcoming sessions',
        value: upcomingCount,
        icon: FiCalendar,
      },
      {
        label: 'Recent sessions',
        value: recentCount,
        icon: FiClock,
      },
      {
  label: 'Student reach',
  value: studentReach,
        icon: FiUsers,
      },
      {
        label: 'Modules in flight',
        value: moduleCount,
        icon: FiBookOpen,
      },
    ]
  }, [formattedUpcoming.length, formattedRecent.length, dashboard])

  const lastSynced = lastUpdated
    ? new Date(lastUpdated).toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        month: 'short',
        day: 'numeric',
      })
    : 'Not synced yet'

  const handleRefresh = () => {
    if (!isLoading) {
      dispatch(fetchStaffDashboard())
    }
  }

  return (
    <div className="lectures-page">
      <header className="lectures-hero">
        <div>
          <p className="eyebrow">Session operations</p>
          <h1>Curate and orchestrate every learning moment.</h1>
          <p className="subhead">Preview upcoming sessions, revisit recent deliveries, and keep your schedule in flow.</p>
        </div>
        <div className="lectures-actions">
          <button type="button" className="refresh-button" onClick={handleRefresh} disabled={isLoading}>
            <FiRefreshCcw size={16} />
            {isLoading ? 'Refreshing…' : 'Refresh data'}
          </button>
          <span className="last-synced">Last synced · {lastSynced}</span>
        </div>
      </header>

      <section className="lectures-stats">
        {stats.map((item) => {
          const Icon = item.icon
          return (
            <article key={item.label} className="stat-card">
              <span className="stat-icon">
                <Icon size={18} />
              </span>
              <div>
                <p>{item.label}</p>
                <strong>{item.value}</strong>
              </div>
            </article>
          )
        })}
      </section>

      <div className="lectures-toolbar">
        <div className="view-toggle" role="tablist" aria-label="Lecture view toggle">
          <button
            type="button"
            role="tab"
            aria-selected={activeView === 'upcoming'}
            className={activeView === 'upcoming' ? 'is-active' : ''}
            onClick={() => setActiveView('upcoming')}
          >
            Upcoming
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeView === 'recent'}
            className={activeView === 'recent' ? 'is-active' : ''}
            onClick={() => setActiveView('recent')}
          >
            Recent
          </button>
        </div>

        <label className="search-field">
          <FiSearch size={18} />
          <input
            type="search"
            placeholder="Search by topic, batch, or module"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </label>

        <Link to="/lectures/new" className="create-link">
          <FiVideo size={16} /> Plan new session
        </Link>
      </div>

      {error && (
        <div className="lectures-error" role="alert">
          Unable to load lecture data: {error}
        </div>
      )}

      {isLoading && !dashboard ? (
        <div className="lectures-state">Loading schedule…</div>
      ) : filteredList.length === 0 ? (
        <div className="lectures-state">
          {baseList.length === 0
            ? `No ${activeView} sessions yet. ${(activeView === 'upcoming' ? 'Schedule your next learning experience.' : 'Check back after your next delivery.')}`
            : 'No sessions match that search query.'}
        </div>
      ) : (
        <section className="lectures-grid" aria-live="polite">
          {filteredList.map((lecture) => (
            <article key={lecture.id} className="lecture-card">
              <header>
                <div>
                  <h2>{lecture.topic}</h2>
                  <p>{lecture.module}</p>
                </div>
                <span className={`status-badge status-${lecture.status.toLowerCase()}`}>{lecture.status}</span>
              </header>
              <div className="lecture-meta">
                <div>
                  <FiCalendar size={16} />
                  <span>{lecture.timing.slot}</span>
                </div>
                <div>
                  <FiClock size={16} />
                  <span>{lecture.timing.duration}</span>
                </div>
                <div>
                  <FiUsers size={16} />
                  <span>{lecture.batch}</span>
                </div>
              </div>
              <footer>
                <span className="lecture-course">{lecture.course}</span>
                <div className="lecture-actions">
                  {lecture.meetingUrl && (
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => window.open(lecture.meetingUrl, '_blank', 'noopener,noreferrer')}
                    >
                      <FiLink size={16} /> Join room
                    </button>
                  )}
                  {lecture.recordingUrl && (
                    <a href={lecture.recordingUrl} className="link-button" target="_blank" rel="noopener noreferrer">
                      <FiVideo size={16} /> View recording
                    </a>
                  )}
                  <Link to={`/lectures/${lecture.id}`} className="detail-link">
                    View details
                  </Link>
                </div>
              </footer>
            </article>
          ))}
        </section>
      )}
    </div>
  )
}

export default Lectures
